/**
 * src/components/pdf/2024/sections/SustainingPlacesPDF.js
 *
 * Sustaining Places section — section cover + content pages.
 *
 * Named exports: SustainingPlacesPage, SustainingPlacesSection
 * Default export: standalone Document (used by the generate script)
 */

import React from 'react';
import { Document, View, Text, Image, Link } from '@react-pdf/renderer';
import { styles, COLORS, registerFonts } from '../styles';
import PageTemplate from '../PageTemplate';
import {
  H1, H2, H3, H4, Body, Section,
  BulletList, SpotlightBox, Footnote,
  SectionCover,
  PageOffsetContext,
} from '../shared';

import sustainingPlacesHero from '../../../ui/corporateresponsibility/impact/2024-impact-report/sustaining-places/sustaining_places_hero.jpg';
import hqImg from '../../../ui/corporateresponsibility/impact/2024-impact-report/sustaining-places/hq.jpg';
import koreaOfficeImg from '../../../ui/corporateresponsibility/impact/2024-impact-report/sustaining-places/korea_office_reno_comp.jpg';
import ghgEmissionsImg from '../../../ui/corporateresponsibility/impact/2024-impact-report/sustaining-places/ghg_emissions_table.png';
import higgFemImg from '../../../ui/corporateresponsibility/impact/2024-impact-report/sustaining-places/higg_fem_score_comparison.png';
import sorelCaribouImg from '../../../ui/corporateresponsibility/impact/2024-impact-report/sustaining-places/sorel_caribou.jpg';
import pranaPackagingImg from '../../../ui/corporateresponsibility/impact/2024-impact-report/sustaining-places/prana_packaging_highlights.jpg';
import pranaShortSleeveImg from '../../../ui/corporateresponsibility/impact/2024-impact-report/sustaining-places/prana_short_sleeve_tee.jpg';
import preferredMaterialsImg from '../../../ui/corporateresponsibility/impact/2024-impact-report/sustaining-places/preferred_materials_graph.jpg';

// Certification badges — use PNG variants where available to reduce file size
import grsBadge from '../../../ui/corporateresponsibility/impact/2024-impact-report/sustaining-places/badges/GRS_Black.png';
import ocs100Badge from '../../../ui/corporateresponsibility/impact/2024-impact-report/sustaining-places/badges/OCS-100_Black.png';
import rcsBadge from '../../../ui/corporateresponsibility/impact/2024-impact-report/sustaining-places/badges/RCS-Blended_Black.png';
import rdsBadge from '../../../ui/corporateresponsibility/impact/2024-impact-report/sustaining-places/badges/RDS_Black.png';
import rocBadge from '../../../ui/corporateresponsibility/impact/2024-impact-report/sustaining-places/badges/ROC_Logo_Black.png';
import rwsBadge from '../../../ui/corporateresponsibility/impact/2024-impact-report/sustaining-places/badges/RWS_Black.png';

import rcs100Img from '../../../ui/corporateresponsibility/impact/2024-impact-report/sustaining-places/badges/RCS-100_Black.jpg';

registerFonts();

// ---------------------------------------------------------------------------
// Sustaining Places content pages
// ---------------------------------------------------------------------------
export function SustainingPlacesPage() {
  return (
    <>
      <PageTemplate sectionTitle="Sustaining Places" accentColor={COLORS.teal}>
        <Section>
          <H1 color={COLORS.teal}>Sustaining Places</H1>
          <H2 color={COLORS.teal}>Climate & Environmental Management</H2>
          <Body>
            At Columbia Sportswear Company ("CSC"), we recognize that global climate change requires
            a collective response as it affects our international communities, ecosystems, and
            outdoor participation.
          </Body>

          <H3>Greenhouse Gas Emissions</H3>
          <Body>
            Since 2022, CSC has heavily invested in improving its ability to accurately calculate
            direct (Scope 1) and indirect emissions from purchased electricity (Scope 2)<Text style={{ fontSize: 6 }}> </Text><Text style={{ fontSize: 8, verticalAlign: 'super', fontWeight: 'bold' }}>2</Text>. These
            efforts have resulted in the Company's most comprehensive Scope 1 and 2 emissions
            inventories to date.
          </Body>
          <Body>
            During this time, CSC has increased its use of primary energy consumption data, even
            as its global retail footprint has grown. Real electricity consumption data now inform
            the majority of Scope 2 calculations. For Scope 1, the Company has refined its
            methodology for estimating emissions from refrigerant leakage, now using recorded
            charge capacities from nearly 300 HVAC units across North America.
          </Body>
          <Body>
            For non-U.S. locations where primary data are unavailable, CSC estimates energy
            consumption based on regional climate type and principal building activity, an approach
            that accounts for operational differences across offices, retail stores, and
            distribution centers. This robust estimation method has improved the accuracy of the
            Company's Scope 1 and 2 emissions inventories compared to prior years.
          </Body>
          <Body>
            These enhancements are reflected in the Company's calculated 2024 Scope 1 and 2
          emissions inventories. The recalculated 2022 inventories (<Link href="https://www.columbiasportswearcompany.com/corporate-responsibility/impact/2024/appendices#appendix-b-greenhouse-gas-emissions-methodology">Appendix B</Link>) also incorporate
            these methodological improvements. CSC continues to refine its methodologies across
            all relevant Scope 3 categories, aligning with the latest scientific standards and
            inventory guidance from the relevant authorities.
          </Body>
        </Section>
      </PageTemplate>
      <PageTemplate sectionTitle="Sustaining Places" accentColor={COLORS.teal}>
        <Section>
          <H4>Scope 1 and 2 Emissions Overview</H4>
          <figure>
            <figcaption style={styles.imageCaption}>
              <Text>
                <Text style={{ textTransform: 'uppercase' }}>GHG Emissions</Text> (metric tons CO2e)
              </Text>
            </figcaption>
            <Image src={ghgEmissionsImg} style={styles.chartImage} />
          </figure>
          <Body>
            Further details on GHG emissions inventorying methodology (and a restatement of the
            Company's prior 2022 scope 1 and scope 2 emissions totals recalculated using this
            updated methodology) are found in <Link href="https://www.columbiasportswearcompany.com/corporate-responsibility/impact/2024/appendices#appendix-b-greenhouse-gas-emissions-methodology">Appendix B</Link>.<Text style={{ fontSize: 6 }}> </Text><Text style={{ fontSize: 8, verticalAlign: 'super', fontWeight: 'bold' }}>3</Text><Text style={{ fontSize: 6 }}> </Text><Text style={{ fontSize: 8, verticalAlign: 'super', fontWeight: 'bold' }}>4</Text>
          </Body>

          <H3>EAC Procurement</H3>
          <Body>
            In early 2024, our Chinese Taipei office underwent major renovations, reducing its
            overall footprint while upgrading the facilities. As part of these changes, we signed
            a green energy lease in partnership with our landlords, allowing the CSC Chinese Taipei
            office to procure 100% renewable energy attribute certificates (EAC) starting in 2025.
          </Body>
          <Body>
            CSC purchased 5,882 MWh of renewable energy certificates (RECs) in 2024.<Text style={{ fontSize: 6 }}> </Text><Text style={{ fontSize: 8, verticalAlign: 'super', fontWeight: 'bold' }}>5</Text> RECs are a
            valuable tool for reducing market-based Scope 2 emissions in our dispersed North
            America retail spaces.
          </Body>

          <H3>CSC Global HQ is Green Business Leaders Certified</H3>
          <Body>
            Since 2021, CSC's global headquarters has been recognized and certified as a <Link href="https://www.washingtoncountyor.gov/swr/green-business-leaders-directory">Green Business Leader</Link> by Washington County, Oregon. During each re-certification cycle, a
            county program director visits CSC's headquarters to review our business practices and
            conduct interviews on environmental stewardship initiatives.
          </Body>
          

          <View>
            <H3>Electric Vans Around CSC Global HQ Campus</H3>
            <View style={{ flexDirection: 'row', gap:24 }}>
              <View style={{ flex:1 }}>
                <Body>
                  In 2024, our Portland global headquarters introduced two custom-built electric
                  passenger transport vans. These vans help on-site team members travel across campus
                  and attend in-person meetings at various buildings. Looking ahead, the Facilities team
                  estimates a savings of approximately 850 gallons of diesel on an annual basis.
                </Body>
              </View>
              <View style={{ flex:1 }}>
                <Image src={hqImg} style={[ styles.bigImage, { aspectRatio: 16 / 9 } ]} />
              </View>
            </View>
          </View>

          <H3>Energy Trust of Oregon Partnership</H3>
          <Body>
            Absolute energy consumption, 2022 versus 2024, at our Oregon distribution center and
            headquarters has declined by over 900,000 kilowatt hours and nearly 67,000 therms
            through a Strategic Energy Management program developed in partnership with the Energy
            Trust of Oregon.<Text style={{ fontSize: 6 }}> </Text><Text style={{ fontSize: 8, verticalAlign: 'super', fontWeight: 'bold' }}>6</Text> Key initiatives, such as photovoltaic panels at our headquarters,
            lighting retrofits, and additional energy efficiency upgrades, have all contributed to
            this ongoing process.
          </Body>

          <H3>Cambrai, France DC Energy Efficiency Gains</H3>
          <Body>
            The Cambrai Distribution Center team in France has worked diligently to implement
            energy-saving procedures that have delivered significant results in reduced combined
            natural gas and electricity consumption per unit processed from 2019 to 2024. Building
            on this progress, our owned distribution centers continue to seek opportunities to
            enhance operations and maximize efficiency.
          </Body>

          <H3>Columbia's Korea Office Renovations</H3>
          <Body>
            In May 2024, the new Columbia Korea office opened with exciting, creative design
            elements. Around the office, rooms feature Plyfix wall panels made from 10% of
            employees' recycled Columbia garment materials and 90% recycled PET bottles, reducing
            the amount of waste sent to landfills.
          </Body>
          <Image src={koreaOfficeImg} style={[styles.inlineImage, { aspectRatio: 16 / 9 }]} />
        </Section>
      </PageTemplate>

      {/* ── Sustainable Manufacturing ─────────────────────────────────────── */}
      <PageTemplate sectionTitle="Sustaining Places" accentColor={COLORS.teal}>
        <Section>
          <H2 color={COLORS.teal}>Sustainable Manufacturing</H2>
          <Body>
            CSC continues to use the Higg Facility Environment Module ("FEM") with strong adoption
            among our finished goods and material suppliers. Managed by Cascale, a global nonprofit
            alliance, the Higg FEM is an environmental assessment and performance management tool
            for textile, apparel, and footwear producers. In 2024, CSC completed its 11th
            consecutive annual reporting cycle using the Higg FEM, with consistently high supplier
            engagement. By measuring key impact areas, the tool helps us prioritize improvements
            in environmental management and remain aware of our overall environmental impact. Higg
            FEM data, along with other metrics, informs our internal scorecards and guides our
            sustainable manufacturing strategy.
          </Body>
          <Image src={higgFemImg} style={{ width: '75%' }} />
          <Text style={[styles.imageCaption, { marginTop: 12 }]}>
            Higg FEM Industry Score Comparison. Scores ranked good/better/best out of 100 across seven impact areas. "CSC" = Tier 1 &amp; 2 factories we partner with; "Industry" = all global facilities responding to the Higg FEM 2023 assessment. For more information on the platform, visit the <Link href="https://howtohigg.cascale.org/higg-index-tools/higg-fem/higg-fem-user-selection/verifiers/an-introduction-to-higg-fem/">Cascale website</Link>.
          </Text>
        </Section>

        <Section>
          <H2 color={COLORS.teal}>Packaging Highlights</H2>
          <Image src={pranaPackagingImg} style={{ width: '70%' }} />
          <Body>
            Across our global packaging procurement, we continue to increase the use of recycled
            content. This includes paper-based packaging made with a minimum of 95% recycled
            material in both corrugated outer cartons and inner boxes in 2024. We've also
            transitioned our polyethylene bags, from 10% post-industrial recycled content to 50%
            post-consumer recycled content, for individual packaging.
          </Body>
          <Body>
            prAna continues to use glassine bags and raffia ties to efficiently package apparel
            while minimizing the use of polyethylene materials. Mountain Hardwear is also expanding
            its use of glassine bags for select styles to enhance the customer experience and
            reduce waste.
          </Body>
          <Body>
            For apparel, equipment, and accessories that require individual polyethylene bags,
            CSC's Spring and Fall 2024 seasons marked progress in reducing both the quantity of
            bags used and transitioning to new materials that generate less waste.
          </Body>
          <Body>
            Furthermore, our manufacturing partners have standardized sportswear apparel fold
            patterns to reduce the amount of polyethylene bag materials used, leading to further
            reductions in packaging waste. Additionally, while business practices and vendor
            management are paramount, our logistics team aims to continuously improve container
            utilization, including increasing carton fill rates by approximately two units per
            carton to reduce empty space.
          </Body>
        </Section>

        <Section>
          <H2 color={COLORS.teal}>Chemical Management</H2>
          <Body>
            In 2024, CSC became a member of the <Link href="https://afirm-group.com/">AFIRM GROUP</Link>, a collaborative organization of
            apparel and footwear companies focused on advancing chemical management throughout the
            global supply chain.
          </Body>
          <Body>
            With this new membership, our Product Compliance team has created a new 2024 <Link href="https://cscworkday.blob.core.windows.net/hrforms/Recruiting/Career_Site/Standards_Policies_Manuals/CSC_RSL&Product_Safety_Manual_English.pdf">Restricted Substances List (RSL)</Link> which blends the strongest aspects of both the AFIRM
            Group and Bluesign RSLs; which all four of CSC's brands will use. This strategic
            initiative reflects a broader commitment to responsible sourcing and operational
            excellence. In parallel, the team continued its engagement with chemical regulatory
            partners through industry group collaboration, upheld the highest industry standards,
            and ensured compliance with ever evolving global regulations.
          </Body>
          <Body>
            Additionally, effective with the Spring 2026 season, we updated our <Link href="https://cscworkday.blob.core.windows.net/hrforms/Recruiting/Career_Site/Standards_Policies_Manuals/CSC_CM_Policy_Guideline_English.pdf">Chemical Management Policy</Link> to align with industry best practices outlined in the Higg FEM 4.0
            chemical module. Our Product Compliance team works with all of our partners to align
            with Higg Fundamental Environmental Performance ("FEP") requirements, which include
            having Chemical Management System ("CMS") policies in place and clearly defined team
            member responsibilities.
          </Body>
          <Body>
            We continue to participate in and support the <Link href="https://www.leatherworkinggroup.com/">Leather Working Group ("LWG")</Link> to promote
            more responsible leather manufacturing across the global textile and apparel industry.
            Sourcing from LWG rated factories ensures that leather suppliers underwent
            manufacturing audits to evaluate chemical, environmental compliance, and performance
            capabilities. 100% of leather materials used in our apparel, footwear, and gloves are
            from LWG rated manufacturing partners.
          </Body>

          <SpotlightBox label="Product Spotlight">
            <View style={{flexDirection: 'row', gap: 24}}>
              <View style={{ flex: 1 }}>
                <Body>
                  Our <Link href="https://www.sorel.com/p/caribou-womens-waterproof-boot-%7C-280-%7C-5-803298145398.html?dwvar_803298145398_color=280">SOREL CARIBOU™ Women's Waterproof Boot</Link>, like all SOREL leather boots, feature
                  100% sourced from LWG gold rated leather manufacturers. This beloved boot has served
                  customers for generations and is built for the toughest environments. By purchasing
                  this product, customers are supporting Leather Working Group certified tanneries.
                </Body>
              </View>
              <View style={{ flex: 1 }}>
                <Image src={sorelCaribouImg} style={styles.inlineImage} />
              </View>
            </View>
          </SpotlightBox>
        </Section>

        <Section>
          <H2 color={COLORS.teal}>Preferred Materials</H2>
          <Body>
            CSC's ongoing commitment to sourcing and incorporating industry-leading preferred
            materials supports positive environmental and social impact across our product value
            chains. Preferred materials commonly featured in the Brand Level Preferred Materials
            Highlights include recycled content, organic content, LWG-certified leather,
            bluesign®-approved materials, RDS down, and RWS wool.
          </Body>
          </Section>
          </PageTemplate>

          <PageTemplate sectionTitle="Sustaining Places" accentColor={COLORS.teal}>
          <Section>
          <View>
            <H3>2024 Brand Level Preferred Material Highlights</H3>
            <Text style={{ fontSize: 10 }}>Percent of styles with at least one preferred material.</Text>
          </View>
          <Image src={preferredMaterialsImg} style={styles.chartImage} />
          <Text style={styles.imageCaption}>
               *Styles with materials that have any percentage of preferred materials are included in this report. This report is based on best available data and may not be inclusive of all preferred attributes especially as it relates to trims, linings, insulations, or footwear bottom and upper components.
            </Text>
        </Section>
      </PageTemplate>

      {/* ── Material Certifications ───────────────────────────────────────── */}
      <PageTemplate sectionTitle="Sustaining Places" accentColor={COLORS.teal}>
        <Section>
          <H3>CSC Material Certifications</H3>
          <Body>
            Working with independent, third-party verification groups helps ensure that our raw
            materials and finished products meet our environmental and social compliance standards.
            To expand our impact across the textile, apparel, and footwear industries, CSC actively
            participates in working groups that develop industry standards and support the global
            growth and adoption of certified materials.
          </Body>

          <H4>Textile Exchange Certification Standards</H4>
          <Body>
            In 2024, we continued to work with our manufacturing partners to achieve the following
            Textile Exchange material certifications in our finished goods.
          </Body>

          <View>
            <H4>Organic Content</H4>
            <Body>
              Building on the launch of <Link href="https://textileexchange.org/organic-content-standard/">Organic Content Standard (OCS)</Link> certified products in 2023,
              prAna expanded the number of OCS-certified products in 2024. Columbia Sportswear
              Company is certified to the Organic Content Standard (OCS), which certifies and tracks
              organically grown material from the source to final product. Certified by Control Union
              CB-CUC-848416. The purchase of Organic Content Standard (OCS) certified products
              demonstrates demand for organic agriculture. Organic cotton is produced and certified
              to organic agricultural standards, which require practices to sustain ecosystems.
            </Body>
            <View style={{ flexDirection: 'row', gap: 24 }}>
              <Image src={rcsBadge} style={{ width: 76, height: 76, objectFit: 'contain' }} />
              <Image src={ocs100Badge} style={styles.badgeImage} />
            </View>
          </View>

          <View>
            <H4>Recycled Content</H4>
            <Body>
              CSC celebrated two new certifications in 2024. Columbia Sportswear Company is certified
              to the <Link href="https://textileexchange.org/recycled-claim-global-recycled-standard/">Recycled Claim Standard (RCS) and Global Recycled Standard (GRS)</Link>, which
              certifies and tracks recycled material from the source to final product. Certified by
              Control Union CB-CUC-848416.
            </Body>
            <BulletList
              items={[
                'GRS: Products certified to the Global Recycled Standard (GRS) contain recycled content that has been independently verified at each stage of the supply chain, from the recycler to the final product. In addition, facilities from the recycler to final supplier have met social, environmental, and chemical requirements.',
                'RCS: Products certified to the Recycled Claim Standard (RCS) contain recycled content that has been independently verified at each stage of the supply chain, from the recycler to the final product.',
              ]}
            />
            <View style={{ flexDirection: 'row', gap: 24 }}>
              <Image src={rcs100Img} style={styles.badgeImage} />
              <Image src={rcsBadge} style={styles.badgeImage} />
              <Image src={grsBadge} style={{ width: 120, objectFit: 'contain' }} />
            </View>
          </View>

          <View>
            <H4>Down</H4>
            <Body>
              Columbia Sportswear Company is certified to the <Link href="https://textileexchange.org/responsible-down-standard/">Responsible Down Standard (RDS)</Link>, which
              certifies and tracks down from the source to final product. Certified by Control Union
              CB-CUC-848416. The Responsible Down Standard (RDS) requires animal welfare practices
              to be in place at duck and goose farms in the down and feather supply chain. RDS has
              been a key component of CSC's preferred materials since becoming certified in 2015.
            </Body>
            <View style={{ flexDirection: 'row', gap: 24 }}>
              <Image src={rdsBadge} style={styles.badgeImage} />
            </View>
          </View>

          <SpotlightBox label="Product Spotlight">
            <Body>
              The Mountain Hardwear <Link href="https://www.mountainhardwear.com/p/womens-storm-whisperer-insulated-jacket-2077711_S.html">Women's Storm Whisperer™ Insulated Jacket</Link> is RDS certified by
              Control Union CB-CUC-848416. The Storm Whisperer™ collection is a great example of
              the certification at work. For adventure seekers who want to get far out into the
              natural world, and stay there longer, this insulated jacket provides the warmth
              needed in order to stay active.
            </Body>
          </SpotlightBox>

          </Section>
          </PageTemplate>

          <PageTemplate sectionTitle="Sustaining Places" accentColor={COLORS.teal}>
          <Section>
          <View>
            <H4>Wool</H4>
            <Body>
              As of 2024, 100% of prAna's virgin wool is (independently) certified to the <Link href="https://textileexchange.org/responsible-wool-standard/">Responsible Wool Standard (RWS)</Link>, certified by Control Union CB-CUC-848416. The
              Responsible Wool Standard (RWS) requires animal welfare and responsible land
              management practices to be in place at the sheep farm.
            </Body>
            <View style={{ flexDirection: 'row', gap: 24 }}>
              <Image src={rwsBadge} style={styles.badgeImage} />
            </View>
          </View>

          <View>
            <H4>Cotton</H4>
            <Body>
              <Link href="https://regenorganic.org/">Regenerative Organic Certified®</Link> Cotton has been used in prAna collections since first
              being introduced in Fall 2023. The Regenerative Organic Certified® is an independent
              third-party certification program, where farms and products meet the world's highest
              standards for soil health, animal welfare, and farmworker fairness, further supporting
              the entire farm ecosystem. Providing vegetative land cover, rotating crops and grazing
              sequentially, minimizing soil disturbances, adding compost, and avoiding synthetic
              fertilizers and pesticides all lead to long-term healthy and highly productive
              agricultural land for growing textile raw materials.
            </Body>
            <View style={{ flexDirection: 'row', gap: 24 }}>
              <Image src={rocBadge} style={{ width: 120, objectFit: 'contain', marginTop: 16 }} />
            </View>
          </View>

          </Section>
          </PageTemplate>
          
        <PageTemplate sectionTitle="Sustaining Places" accentColor={COLORS.teal}>
        <Section>

          <SpotlightBox label="Product Spotlight">
            <View style={{ flexDirection: 'row', gap: 24 }}>
              <View style={{ flex: 1 }}>
                <Body>
                  prAna's <Link href="https://www.prana.com/p/everyday-short-sleeve-tee/2063791.html?dwvar_2063791_size=M&dwvar_2063791_color=Carob">Everyday Short Sleeve Tee</Link> is 100% Regenerative Organic Certified® Bronze
                  Cotton. In fact, prAna's entire Everyday collection of 18 unique tanks and tee styles
                  were made with certified cotton! The relaxed fits have a beautiful vintage wash that
                  keeps you comfortable all day and gets better with every wear.
                </Body>
              </View>
              <View style={{ flex: 1 }}>
                <Image src={pranaShortSleeveImg} style={styles.inlineImage} />
              </View>
            </View>
          </SpotlightBox>
        </Section>

        <Section>
          <H4>CanopyStyle & Pack4Good</H4>
          <Body>
            We are proud to continue our commitment to protecting Ancient and Endangered Forests.
            When prAna joined the CanopyStyle initiative in 2013 and later became a signatory of
            Canopy's Pack4Good initiative in 2019, our commitments prioritized product packaging
            decisions and responsible sourcing of materials.
          </Body>
          <Body>
            CanopyStyle unites fashion brands, retailers, designers, and producers of manmade
            cellulosic fibers (MMCF) in a shared mission to eliminate Ancient and Endangered
            Forests from the textile supply chain. As part of this global movement, prAna is
            helping to reduce demand for materials sourced from some of the world's most threatened
            forest ecosystems.
          </Body>
          <Body>
            Through Canopy's <Link href="https://canopyplanet.org/change-together/pack4good">Pack4Good</Link> initiative, prAna is also working to ensure its packaging
            avoids fiber sourced from Ancient and Endangered Forests, and where possible uses
            high-recycled content or other high-performance agricultural residue alternatives —
            helping to build a more sustainable, climate-resilient future.
          </Body>

          <H4>Forest Stewardship Council Certification (FSC)</H4>
          <Body>
            prAna continued producing yoga mats that feature a blend of FSC materials and recycled
            rubber for our year-round collections. Specifically, the <Link href="https://www.prana.com/p/verde-yoga-mat/1965561.html">prAna Verde Yoga Mat</Link> top layer
            is made from 100% FSC-certified rubber, the middle layer is 100% Organic Cotton, and
            the bottom layer is 65% FSC-certified rubber and 35% recycled rubber. These premium,
            environmentally conscious materials have positive manufacturing impacts.
          </Body>
        </Section>

        <Section>
          <H2 color={COLORS.teal}>Products Made to Last</H2>
          <Body>
            Design for Durability: Durability is a core design principle of the apparel, footwear,
            equipment, and accessories across the CSC brands. We work closely with product testers
            and manufacturing partners to ensure our products are thoroughly tested and built to
            last.
          </Body>
          <Body>
            Repair: Mountain Hardwear has a dedicated Warranty team that repairs products in order
            to extend their useful life.
          </Body>
          <Body>
            Donate: Product that cannot be sold gets donated to non-profit organizations, including
            schools, shelters, and veteran support groups. In 2024, we donated over 3,000 mixed
            boxes of apparel and footwear to over 20 community organization partners.
          </Body>

          <SpotlightBox label="Product Spotlight">
            <Body>
              OUTGROWN™ Extend System collection: This collection of jackets, pants, and other
              apparel is designed to grow with kids as their arms and legs grow over time. Our
              innovative OUTGROWN™ Extend System adds up to 1.5 inches in length, helping jackets
              and pants last through growth spurts. A simple snip of the internal seam and removal
              of the internal thread significantly extends the life of the product, giving it more
              wear and value over time. <Link href="https://www.columbia.com/c/outgrown/">Learn more.</Link>
            </Body>
          </SpotlightBox>
        </Section>

        <View style={styles.footnoteSection}>
          <Footnote
            num={2}
            text="Scope 1 emissions are the direct greenhouse gas emissions from sources that are owned or controlled by CSC. These include emissions from our company-owned vehicles and other on-site combustion sources. Scope 2 emissions are the indirect greenhouse gas emissions from the consumption of purchased electricity, steam, heat, and cooling."
          />
          <Footnote
            num={3}
            text="Scope 2 The LB (location-based) method to calculate scope 2 emissions considers average emission factors for the electricity grids that provide electricity (i.e. the grid's emissions factor)."
          />
          <Footnote
            num={4}
            text="Scope 2 The MB (market-based) method to calculate scope 2 emissions considers contractual arrangements under which the organization procures power from specific sources, such as purchasing renewable energy credits (RECs)."
          />
          <Footnote
            num={5}
            text="2024 REC retirements are reflected in CSC's 2023 market-based scope 2 emissions total."
          />
          <Footnote
            num={6}
            text="Absolute annual energy consumption, 2022 versus 2024, at our Oregon distribution center declined by over 50,000 therms and nearly half-a-million kilowatt hours. These numbers reflect billed energy consumption totals."
          />
        </View>
      </PageTemplate>
    </>
  );
}

// ---------------------------------------------------------------------------
// Section composite — cover + content (for use in the full assembled report)
// ---------------------------------------------------------------------------
export function SustainingPlacesSection() {
  return (
    <>
      <SectionCover
        bgImage={sustainingPlacesHero}
        accentColor={COLORS.teal}
        title="Sustaining Places"
        subtitle="A thriving planet is essential for the well-being of individuals and communities. Our
            commitment is to operate responsibly to preserve the environments where we live, work,
            and play."
        quote="Now more than ever, it's important that we protect our outdoor spaces."
        attribution="— Abel Navarrete, VP, Sustainability and Community Impact"
      />
      <SustainingPlacesPage />
    </>
  );
}

// ---------------------------------------------------------------------------
// Standalone Document (used by the generate script)
// ---------------------------------------------------------------------------
export default function SustainingPlacesPDF({ pageOffset = 0, totalDocPages = null } = {}) {
  return (
    <PageOffsetContext.Provider value={{ pageOffset, totalDocPages }}>
      <Document
        title="2024 CSC Impact Report — Sustaining Places"
        author="Columbia Sportswear Company"
      >
        <SustainingPlacesSection />
      </Document>
    </PageOffsetContext.Provider>
  );
}
